import { useState } from 'react';
import { useDesignStore } from '@/store/designStore';
import { Design, DesignVersion, DesignFile, DesignTask } from '@/types/design';
import { useToast } from './useToast';

export const useDesignManagement = () => {
  const [isLoading, setIsLoading] = useState(false);
  const designStore = useDesignStore();
  const { toast } = useToast();

  const handleError = (error: unknown) => {
    const message = error instanceof Error ? error.message : 'An error occurred';
    toast({
      title: "Error",
      description: message,
      variant: "destructive"
    });
    throw error;
  };

  const createDesign = async (data: Partial<Design>) => {
    try {
      setIsLoading(true);
      await designStore.createDesign(data);
      toast({
        title: "Success",
        description: "Design created successfully"
      });
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateDesign = async (id: string, data: Partial<Design>) => {
    try {
      setIsLoading(true);
      await designStore.updateDesign(id, data);
      toast({
        title: "Success",
        description: "Design updated successfully"
      });
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const createVersion = async (designId: string, data: Partial<DesignVersion>) => {
    try {
      setIsLoading(true);
      await designStore.createVersion(designId, data);
      toast({
        title: "Success",
        description: "New version created successfully"
      });
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const uploadFile = async (file: File, designId: string, versionId: string) => {
    try {
      setIsLoading(true);
      await designStore.uploadFile(file, designId, versionId);
      toast({
        title: "Success",
        description: "File uploaded successfully"
      });
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const createTask = async (data: Partial<DesignTask>) => {
    try {
      setIsLoading(true);
      await designStore.createTask(data);
      toast({
        title: "Success",
        description: "Task created successfully"
      });
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateTask = async (id: string, data: Partial<DesignTask>) => {
    try {
      setIsLoading(true);
      await designStore.updateTask(id, data);
      toast({
        title: "Success",
        description: "Task updated successfully"
      });
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    createDesign,
    updateDesign,
    createVersion,
    uploadFile,
    createTask,
    updateTask,
    ...designStore
  };
};